'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
  SESSION_LOST_CONFIRMED_EVENT,
  isLoginLikePath,
  loginPathFor,
  onSessionLost,
} from '@/lib/session-lost';

const REDIRECT_DELAY_MS = 1800;

/**
 * Luistert naar sessieverlies (401 op eigen API's, SSE-afbraak), bevestigt bij de server
 * en stuurt dan naar het juiste inlogscherm. Toont kort een banner zodat de gebruiker
 * begrijpt waarom hij opnieuw moet inloggen. Gemount in de root-layout, dus overal actief.
 */
export function SessionLostGuard() {
  const pathname = usePathname();
  const t = useTranslations('sessionLost');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!pathname || isLoginLikePath(pathname)) return;
    let handling = false;
    return onSessionLost(async (source) => {
      if (handling) return;
      handling = true;
      try {
        const res = await fetch('/api/auth/session', { credentials: 'include', cache: 'no-store' });
        if (res.ok) {
          const data = (await res.json().catch(() => null)) as { session?: unknown } | null;
          if (data && data.session) {
            // Vals alarm (hik, deploy): sessie bestaat nog.
            handling = false;
            return;
          }
        } else if (res.status !== 401) {
          // Server-/netwerkfout: niets bewijzen, niet redirecten.
          handling = false;
          return;
        }
      } catch {
        handling = false;
        return;
      }

      // Bevestigd: concepten laten bewaren, tellen, banner, redirect.
      window.dispatchEvent(new CustomEvent(SESSION_LOST_CONFIRMED_EVENT, { detail: { source } }));
      void fetch('/api/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'SESSION_LOST' }),
        keepalive: true,
      }).catch(() => undefined);
      setVisible(true);
      window.setTimeout(() => {
        window.location.assign(loginPathFor(window.location.pathname, window.location.search));
      }, REDIRECT_DELAY_MS);
    });
  }, [pathname]);

  if (!visible) return null;
  return (
    <div
      role="status"
      className="fixed inset-x-0 top-0 z-[1000] bg-amber-500 px-4 py-3 text-center text-sm font-medium text-white shadow-md"
    >
      {t('banner')}
    </div>
  );
}
